import React, { useState, useRef, useEffect } from 'react';
import useSpeechToText from 'react-hook-speech-to-text';
import SimpleAudioVisualizer from './AudioWave';
import { AspectRatio, Box, Card, IconButton, ModalClose, Stack, Typography } from '@mui/joy';
import { IoMdClose, IoMdMic, IoMdMicOff } from "react-icons/io";
import { FaVideo } from 'react-icons/fa';
import { BsThreeDots } from 'react-icons/bs';
import { motion } from 'framer-motion';
import { getApiUrl } from '../utils/api';

const isRussianDomain = window.location.hostname === 'chat.acceptancegroup.org';

export default function VoiceChat({ isMobile, chatId, version, temperature, user, isVoiceChat, systemInstructions, customModel, close, voiceName }) {
    const videoRef = useRef(null);

    const [lastResult, setLastResult] = useState(null);
    const [previousResult, setPreviousResult] = useState(null);
    const [lastSentResult, setLastSentResult] = useState(null);
    const [waitBot, setWaitBot] = useState(false);
    const [isVideo, setIsVideo] = useState(false);
    const [stream, setStream] = useState(null);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const mediaRecorderRef = useRef(null);

    const [messageId, setMessageId] = useState(null);

    const getSupportedMimeType = () => {
        const mimeTypes = [
            'video/webm;codecs=vp9',
            'video/webm;codecs=vp8',
            'video/webm',
            'video/mp4',
        ];
        return mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || '';
    };

    const startStream = async () => {
        try {
            stopStream();
            let newStream;
            if (isMobile) {
                try {
                    newStream = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: "environment" },
                    });
                } catch {
                    newStream = await navigator.mediaDevices.getUserMedia({ video: true });
                }
            } else {
                newStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true,
                });
            }

            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
                videoRef.current.play();
                setIsVideo(true);
            }
            setStream(newStream);

            const mimeType = getSupportedMimeType();
            if (!mimeType) {
                throw new Error('No supported MIME type available for MediaRecorder');
            }
            console.log('Mime: ' + mimeType);
            const options = {
                mimeType,
                videoBitsPerSecond: 500000,
            };

            const mediaRecorder = new MediaRecorder(newStream, options);
            mediaRecorder.ondataavailable = handleDataAvailable;
            mediaRecorder.onstop = handleStop;
            mediaRecorderRef.current = mediaRecorder;
        } catch (err) {
            console.error("Error accessing media devices:", err.name, err.message);
            if (err.name === "NotAllowedError") {
                console.error("Permissions denied by user.");
            } else if (err.name === "NotFoundError") {
                console.error("No camera available.");
            } else if (err.name === "OverconstrainedError") {
                console.error("Camera constraints not met:", err.constraint);
            }
        }
    };

    const stopStream = () => {
        setIsVideo(false);
        if (stream) {
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
            setStream(null);
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const handleDataAvailable = (event) => {
        if (event.data.size > 0) {
            setRecordedChunks((prev) => [...prev, event.data]);
        }
    };

    const startScreenRec = () => {
        if (mediaRecorderRef.current && stream) {
            mediaRecorderRef.current.start();
        }
    };

    const stopScreenRec = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }
    };

    useEffect(() => {
        try {
            stopSpeechToText();
            handleStop();
        }
        catch (ex) {
            console.log(ex);
        }
    }, [isVoiceChat]);

    const {
        error,
        interimResult,
        isRecording,
        results,
        startSpeechToText,
        stopSpeechToText,
    } = useSpeechToText({
        continuous: true,
        useLegacyResults: false,
    });

    const startRecording = () => {
        startSpeechToText();
    };

    const formatStringWithoutMarkdown = (input) => {
        let text = input
            .replace(/(\*\*|__)(.*?)\1/g, '$2')
            .replace(/(\*|_)(.*?)\1/g, '$2')
            .replace(/~~(.*?)~~/g, '$1')
            .replace(/!\[.*?\]\(.*?\)/g, '')
            .replace(/\[.*?\]\(.*?\)/g, '')
            .replace(/```[\s\S]*?```/g, '')
            .replace(/`([^`]+)`/g, '$1')
            .replace(/^\s*#\s*/gm, '')
            .replace(/^\s*>.*$/gm, '')
            .replace(/^\s*[-*+]\s+/gm, '')
            .replace(/^\s*\d+\.\s+/gm, '')
            .replace(/(?:\r?\n){2,}/g, '\n')
            .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
            .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
            .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
            .replace(/[\u{1F700}-\u{1F77F}]/gu, '')
            .replace(/[\u{1F780}-\u{1F7FF}]/gu, '')
            .replace(/[\u{1F800}-\u{1F8FF}]/gu, '')
            .replace(/[\u{1F900}-\u{1F9FF}]/gu, '')
            .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '')
            .replace(/[\u{2600}-\u{26FF}]/gu, '')
            .replace(/[\u{2700}-\u{27BF}]/gu, '')
            .trim();

        if (isRussianDominant(text)) {
            text = text
                .replace(/Capricorn/g, "Каприкорн")
                .replace(/SCREW LTD\./g, "скрю элтэдэ.")
                .replace(/REMNANT/g, "ремнант")
                .replace(/SCREW: WS/g, "скрю виэс")
                .replace(/SCREW: EDITOR/g, "скрю эдитор")
                .replace(/SCREW:/g, "скрю")
                .replace(/SCREW/g, "скрю")
        }

        text = text.replace(/SCREW:/g, "SCREW");

        return text;
    };

    const isRussianDominant = (input) => {
        const cyrillicPattern = /[А-Яа-яЁё]/g;
        const latinPattern = /[A-Za-z]/g;

        const cyrillicCount = (input.match(cyrillicPattern) || []).length;
        const latinCount = (input.match(latinPattern) || []).length;

        return cyrillicCount > latinCount;
    };

    useEffect(() => {
        if (isVoiceChat) {
            setMessageId(null);
            sendSystemMessage();
        }
        else {
            if (messageId != null) {
                sendSystemMessage();
            }
        }
    }, [isVoiceChat]);

    const sendSystemMessage = async () => {
        if(error)
            return;
        
        const url = getApiUrl('/v3/ai/system-message');

        const payload = {
            chatId: chatId,
            messageId: messageId || null,
            type: 'voice'
        };

        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.jwt_token}`,
            },
            body: JSON.stringify(payload),
        };

        try {
            const response = await fetch(url, options);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to send system message');
            }

            const data = await response.json();
            console.log(data);
            setMessageId(data?.messageId ? data.messageId : null);
        } catch (error) {
            console.error('Error:', error.message);
        }
    };

    const sendMessage = async (message) => {
        if (user == null)
            return;

        setWaitBot(true);

        try {
            let apiUrl = getApiUrl('/v3/ai/generate');

            if (customModel) {
                apiUrl = getApiUrl(`/v3/ai/generate/${customModel.id}`);
            }

            const { password, jwt_token, avatar, ...userWithoutSensitiveInfo } = user;

            let file = null;
            if (isVideo) {
                const blob = new Blob(recordedChunks, { type: 'video/webm' });
                const guid = crypto.randomUUID();
                file = new File([blob], `capricorn_capture_${guid}.webm`, { type: 'video/webm' });
            }
            const formData = new FormData();

            const requestBody = {
                message: message,
                chatId: chatId,
                temperature: parseFloat(temperature),
                version: version,
                voice: customModel?.voice ? customModel?.voice : voiceName,
                userData: JSON.stringify(userWithoutSensitiveInfo),
                instructions: `You are on the website ${isRussianDomain ? 'https://chat.acceptancegroup.org/' : 'https://chat.screwltd.com/'} — This is a portal for testing neural networks and various technologies from ${isRussianDomain ? 'Acceptance Group LLC' : 'SCREW LTD.'}, available to all users registered through SCREW: ID.
                
                At the moment you are in a voice chat with the user, you have the ability to see his camera or screen sharing depending on the device and resolution of the user. 
                Answer as briefly and humanely as possible, your answers should be as similar as possible to the answers of an ordinary person. 
                Short and clear.
                
                ${systemInstructions}`,
            };

            for (const key in requestBody) {
                formData.append(key, requestBody[key]);
            }

            if (file) {
                formData.append('files', file);
            }

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user.jwt_token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            handleGenerateAudio(response);
            setLastSentResult(message);
        } catch (error) {
            setWaitBot(false);
        }
    };

    const handleSpeechEnd = (result) => {
        const transcript = String(result.transcript);
        if (transcript !== lastSentResult) {
            sendMessage(transcript);
        }
    };

    const [timer, setTimer] = useState(null);

    useEffect(() => {
        if (results.length > 0) {
            setPreviousResult(lastResult);
            setLastResult(results[results.length - 1]);

            if (timer) {
                clearTimeout(timer);
            }

            const newTimer = setTimeout(() => {
                if (previousResult) {
                    stopSpeechToText();
                    handleSpeechEnd(previousResult);
                }
            }, 100);

            setTimer(newTimer);
        }
    }, [results, previousResult, lastResult]);

    const ttsRef = useRef(null);
    const [ttsPlaying, setTtsPlaying] = useState(false);

    const handleGenerateAudio = async (response) => {
        setTtsPlaying(false);
        try {
            const arrayBuffer = await response.arrayBuffer();
            const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
            const audioBlobUrl = URL.createObjectURL(blob);

            console.log(response);

            if (!ttsRef.current) {
                ttsRef.current = new Audio();
            }

            ttsRef.current.src = audioBlobUrl;
            ttsRef.current.play().then(() => {
                setTtsPlaying(true);
            }).catch((error) => {
                console.error('Error playing audio:', error);
                setTtsPlaying(false);
            });

            ttsRef.current.onended = () => {
                setTtsPlaying(false);
                startRecording();
            };
            ttsRef.current.onerror = () => setTtsPlaying(false);

        } catch (error) {
            console.error('Error fetching audio:', error);
        } finally {
            setWaitBot(false);
        }
    };

    const handleStop = () => {
        if (ttsRef.current) {
            ttsRef.current.pause();
            ttsRef.current.currentTime = 0;
        }
        setTtsPlaying(false);
    };

    useEffect(() => {
        if (isVideo) {
            if (isRecording) {
                startScreenRec();
            }
            else {
                stopScreenRec();
            }
        }
    }, [isRecording]);

    if (error) return <Box><ModalClose sx={{mt: (window.__TAURI_INTERNALS__ ? '30px' : 0), borderRadius: 250}}/> <Typography>Capricorn Live API is not available in this client.</Typography></Box>;

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', width: '100%', flex: '1' }}>
                <motion.div
                    animate={{
                        height: isVideo ? '16%' : '100%',
                    }}
                    transition={{
                        duration: 0.5,
                        ease: 'easeInOut',
                    }}
                    style={{ width: '100%' }}
                >
                    <SimpleAudioVisualizer ttsRef={ttsRef} style={{ width: '100%', height: '100%' }} />
                </motion.div>

                <motion.div
                    animate={{
                        height: isVideo ? '84%' : '0%',
                        opacity: isVideo ? 1 : 0,
                    }}
                    transition={{
                        duration: 0.5,
                        ease: 'easeInOut',
                    }}
                    style={{
                        width: '99%',
                        position: 'absolute',
                        bottom: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Card
                        variant="soft"
                        sx={{
                            width: isMobile ? '100%' : 'auto',
                            aspectRatio: isMobile ? 'auto' : '16/9',
                            height: '100%',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            boxSizing: 'border-box',
                            p: 0,
                            overflow: 'hidden'
                        }}
                    >
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Card>
                </motion.div>
            </div>

            <Stack
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 'auto',
                    mt: 4,
                }}
                direction="row"
                spacing={1}
            >
                <IconButton
                    color={isVideo ? 'primary' : 'neutral'}
                    variant="soft"
                    onClick={() => isVideo ? stopStream() : startStream()}
                    sx={{
                        borderRadius: 250,
                        width: 50,
                        height: 50,
                        fontSize: 22,
                    }}
                >
                    <FaVideo />
                </IconButton>
                <IconButton
                    disabled={waitBot}
                    loading={waitBot}
                    color={isRecording ? 'danger' : 'neutral'}
                    onClick={isRecording ? stopSpeechToText : () => { startRecording(); handleStop(); }}
                    variant="soft"
                    sx={{
                        borderRadius: 250,
                        width: 50,
                        height: 50,
                        fontSize: 24,
                    }}
                >
                    {isRecording ? <IoMdMic /> : <IoMdMicOff />}
                </IconButton>
                <IconButton
                    color="neutral"
                    variant="soft"
                    disabled
                    sx={{
                        borderRadius: 250,
                        width: 50,
                        height: 50,
                        fontSize: 24,
                    }}
                >
                    <BsThreeDots />
                </IconButton>
                <IconButton
                    color="neutral"
                    variant="soft"
                    onClick={() => { handleStop(); close(); if (isVideo) { stopStream(); } }}
                    sx={{
                        borderRadius: 250,
                        width: 50,
                        height: 50,
                        fontSize: 22,
                    }}
                >
                    <IoMdClose />
                </IconButton>
            </Stack>
        </div>
    );
}
